import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { useNavigate, useParams } from 'react-router-dom';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useEffect } from 'react';
import { noop } from 'lodash';
import { useForm } from '../../../components/Form/hooks';
import {
  AtividadeForm,
  defaultValues,
  tiposFilter,
  validationSchema,
} from './form-controller';
import Agro1Loader from '../../../components/Agro1Loader';
import AtividadesService, {
  AtividadeToSave,
} from '../../../services/atividades';
import Agro1Toast from '../../../components/Agro1Toast';
import useAsyncFetch from '../../../hooks/use-async';
import { beforeExit } from '../../utils';
import { AtividadeFormValues } from '../types';
import { getMessagesAtividades } from '../utils';

const AtividadeCreateEditPage = () => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const formObject = useForm<AtividadeFormValues>({
    validationSchema,
    defaultValues,
  });
  const { reset, handleSubmit, getValues } = formObject.form;

  const exit = () => {
    navigate('/gerenciador-dados/atividades');
  };

  const { data: atividade } = useAsyncFetch(
    {
      withLoader: true,
      callback: async () => {
        if (params.id) return AtividadesService.find(params.id);
      },
      errorMessage: 'Falha ao carregar o registro para edição.',
      onError: exit,
    },
    [params.id],
  );

  useEffect(() => {
    if (!atividade) return;

    reset({
      ...getValues(),
      id: atividade.id,
      descricao: atividade.descricao,
      cor: atividade.cor,
      corFonte: atividade.corFonte,
      tipo: atividade.tipo,
      abreviatura: atividade.abreviatura,
    });
  }, [atividade, reset, getValues]);

  const onSubmit = async () => {
    const formValue = getValues();
    const loaderId = Agro1Loader.show();

    try {
      const record: AtividadeToSave = {
        abreviatura: formValue.abreviatura!.toUpperCase(),
        cor: formValue.cor,
        corFonte: formValue.corFonte,
        descricao: formValue.descricao!.toUpperCase(),
        tipo: formValue.tipo!,
      };

      if (params.id) {
        await AtividadesService.update({ ...record, id: params.id });
      } else {
        await AtividadesService.create(record);
      }

      exit();

      Agro1Toast.show({
        detail: 'Registro salvo com sucesso.',
        severity: 'success',
      });
    } catch (error) {
      Agro1Toast.show({
        detail: getMessagesAtividades(error, params.id ? 'edit' : 'create'),
        severity: 'error',
        sticky: true,
      });
    } finally {
      Agro1Loader.close(loaderId);
    }
  };

  return (
    <div className="container w-full gerenciador-dados-create-edit-container">
      <div className="flex flex-wrap mt-6 mx-3">
        <div className="text-3xl font-normal">
          {atividade ? 'Editar' : 'Nova'} Atividade
        </div>
      </div>
      <ConfirmDialog />
      <div>
        <Panel className="mt-4 mx-3">
          <div className="col-12 m-auto">
            <AtividadeForm
              formObject={formObject}
              submitByEnter={false}
              onSubmit={noop}
            >
              <div className="col-12 flex align-items-center pb-0">
                <AtividadeForm.InputTextarea
                  name="descricao"
                  label="Descrição:"
                  containerClassName="col-6"
                  className="w-full"
                  rows={1}
                />
                <AtividadeForm.InputTextarea
                  name="abreviatura"
                  label="Abreviatura:"
                  containerClassName="col-6"
                  className="w-full"
                  rows={1}
                />
              </div>
              <div className="col-12 flex align-items-center">
                <AtividadeForm.Dropdown
                  name="tipo"
                  label="Tipo:"
                  options={tiposFilter}
                  dataKey="id"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Tipo"
                  containerClassName="col-6"
                />
                <AtividadeForm.ColorPicker
                  name="cor"
                  label="Cor:"
                  containerClassName="ml-2 mr-3"
                  defaultColor="0000"
                />
                <AtividadeForm.ColorPicker
                  name="corFonte"
                  label="Cor da Fonte:"
                  containerClassName="mx-3"
                  defaultColor="0000"
                />
              </div>
            </AtividadeForm>
          </div>
        </Panel>
        <div className="flex justify-content-end mt-2 mx-3">
          <Button
            type="button"
            className="button-danger mr-1"
            label="Cancelar"
            onClick={() => beforeExit({ onConfirm: exit })}
          />
          <Button
            type="submit"
            className="button-secondary"
            label="Salvar"
            onClick={() => handleSubmit(onSubmit)()}
          />
        </div>
      </div>
    </div>
  );
};

export default AtividadeCreateEditPage;
